/** @jsxImportSource @emotion/react */
import React from "react";
import {useTranslation} from "react-i18next";
import {Icon, Tag} from "@blueprintjs/core";

import {useAuth} from "../auth/auth";
import {useUserClientsData} from "../user-clients-data/user-clients-data";
import {AVAILABLE_SERVICES} from "./services-config";

// Components
import ClientSelector from "../common/components/client-selector";
import Button from "../common/components/button";
import AppToaster from "../common/components/toaster";

// Libs
import {boxShadow, primaryColor, textColor, activeColor} from "../common/libs/theme-lib";
import emptyImage from "../assets/undraw_empty_xct9.svg";

const inputBoxStyle = {
    background: "#f5f5f5",
    padding: "12px",
    borderRadius: 4,
    marginTop: 4,
    wordBreak: "break-all",
    border: "1px solid #cdcdcd",
    color: "#696969",
    fontWeight: 400,
    fontSize: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
}

const iconCopyStyle = {
    cursor: "pointer",
    color: "#696969",
    flexShrink: 0
}

const ServicesPage = () => {
    const {t} = useTranslation("services");
    const {selectedClient, userType} = useAuth();
    const {userClientsData} = useUserClientsData();

    const clientData = userClientsData.find(client => client._id === selectedClient);
    const services = clientData?.services || [];

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            AppToaster.show({ message: t("copied"), intent: "success" });
        } catch {
            AppToaster.show({ message: t("copyError"), intent: "danger" });
        }
    };

    const getServiceConfig = (serviceName) => {
        return AVAILABLE_SERVICES.find(s => s.service_name === serviceName);
    };

    return (
        <div>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold",
                        marginBottom: 10
                    }}>{t("title", { client: clientData && clientData.name })}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                {userType === "admin" && <ClientSelector />}
            </div>
            <div style={{
                backgroundColor: "white",
                boxShadow: boxShadow,
                padding: 44,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                color: textColor,
                gap: 15
            }}>
                {services.length === 0 ? (
                    <div style={{
                        display: "block",
                        margin: "auto"
                    }}>
                        <img
                            src={emptyImage}
                            alt="Sin servicios"
                            style={{
                                maxWidth: "300px",
                                display: "block",
                                margin: "auto auto 40px auto"
                            }}
                        />
                        <div style={{fontWeight: 100, fontSize: 18}}>
                            {t("noServices")}
                        </div>
                    </div>
                ) : services.map((service, index) => {
                    const config = getServiceConfig(service.service_name);
                    const apiUrl = `${window.location.origin + config.url}`;
                    return (
                        <div
                            key={index}
                            style={{
                                backgroundColor: "white",
                                border: "1px solid #e9e9e9",
                                padding: 22,
                                width: "100%",
                                display: "flex",
                                flexDirection: "column"
                            }}
                        >
                            <div style={{display: "flex"}}>
                                <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                                    <span style={{margin: "0 0 10px 0", fontWeight: "bold", fontSize: 18}}>
                                        {config ? t(config.name) : service.service_name}
                                    </span>
                                    <p style={{margin: "0 0 10px 0", fontSize: 16, lineHeight: "24px", color: "#373737"}}>
                                        {config ? t(config.description) : ""}
                                    </p>
                                </div>
                                <div>
                                    {service.active ? (
                                        <Tag
                                            style={{ minWidth: "80px", textAlign: "center", backgroundColor: activeColor }}
                                            large={true}
                                            intent={"success"}
                                        >
                                            {t("active")}
                                        </Tag>
                                    ) : (
                                        <Tag
                                            style={{ minWidth: "80px", textAlign: "center" }}
                                            large={true}
                                            intent={"info"}
                                        >
                                            {t("inactive")}
                                        </Tag>
                                    )}
                                </div>
                            </div>
                            <div style={{display: "flex", gap: 14}}>
                                <div style={{marginBottom: 10, flex: 1}}>
                                    <span style={{fontSize: 12}}>{t("apiToken")}</span>
                                    <div style={inputBoxStyle}>
                                        <span>{service.api_token}</span>
                                        <Icon icon="duplicate" style={iconCopyStyle} onClick={() => handleCopy(service.api_token)} />
                                    </div>
                                </div>
                                <div style={{marginBottom: 10, flex: 1}}>
                                    <span style={{fontSize: 12}}>{t("apiUrl")}</span>
                                    <div style={inputBoxStyle}>
                                        <span>{apiUrl}</span>
                                        <Icon icon="duplicate" style={iconCopyStyle} onClick={() => handleCopy(apiUrl)} />
                                    </div>
                                </div>
                            </div>
                            <p style={{margin: "0 0 10px 0", fontSize: 16, lineHeight: "24px", color: "#373737"}}>
                                {config ? t(config.guide) : ""}
                            </p>
                            <Button
                                text={t("downloadPDF")}
                                icon="download"
                                intent="primary"
                                style={{
                                    width: "fit-content"
                                }}
                                action={() => window.open(config.guide_url, '_blank')}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ServicesPage;
